import React from 'react';
import { FormattedMessage } from '../../../../../util/reactIntl';
import css from './TrackYourPath.module.css';

/**
 * Track Your Path — /p/track-your-path
 *
 * Self-contained section component. Content is hardcoded from the approved
 * Website Copy v1.0 document (4 Sep 2026). Rendered inside PageBuilder via
 * SectionContainer when the CMS section-id matches.
 *
 * DO NOT PUBLISH until the learning-system build (placement report, level
 * certificate, progress emails) is delivered and tested.
 */
const TrackYourPath = () => {
  return (
    <article className={css.pageWrapper}>
      {/* ── Hero / Intro ── */}
      <header className={css.heroSection}>
        <h1 className={css.pageTitle}>
          <FormattedMessage
            id="TrackYourPath.title"
            defaultMessage="You will always know how your child is doing"
          />
        </h1>
        <p className={css.introText}>
          <FormattedMessage
            id="TrackYourPath.intro1"
            defaultMessage="The hardest part of paying for lessons is not the money. It is not knowing whether they are working."
          />
        </p>
        <p className={css.introAccent}>
          <FormattedMessage
            id="TrackYourPath.intro2"
            defaultMessage="Arabibi is built so that a parent can see progress — not guess at it, and not rely on their child's own account of the lesson."
          />
        </p>
      </header>

      {/* ── How it works — steps table ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="TrackYourPath.howItWorks.title"
            defaultMessage="How it works, from the first lesson"
          />
        </h2>

        <div className={css.tableWrapper}>
          <table className={`${css.table} ${css.stepsTable}`}>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="TrackYourPath.steps.step" defaultMessage="Step" />
                </th>
                <th>
                  <FormattedMessage id="TrackYourPath.steps.what" defaultMessage="What" />
                </th>
                <th>
                  <FormattedMessage id="TrackYourPath.steps.happens" defaultMessage="What happens" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className={css.stepNumber}>1</span></td>
                <td>
                  <FormattedMessage id="TrackYourPath.step1.what" defaultMessage="A starting picture" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.step1.desc" defaultMessage="Before the first lesson you answer a few short questions about your child's Arabic. That gives us a provisional starting level for each of the five skills." />
                </td>
              </tr>
              <tr>
                <td><span className={css.stepNumber}>2</span></td>
                <td>
                  <FormattedMessage id="TrackYourPath.step2.what" defaultMessage="The trial confirms it" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.step2.desc" defaultMessage="In the free trial lesson, the tutor assesses your child properly and confirms or corrects that starting point. You receive it in writing: where your child is on each skill, and what the tutor would work on first." />
                </td>
              </tr>
              <tr>
                <td><span className={css.stepNumber}>3</span></td>
                <td>
                  <FormattedMessage id="TrackYourPath.step3.what" defaultMessage="Every lesson is recorded" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.step3.desc" defaultMessage="After each lesson the tutor completes a short report — what was covered, which objectives were achieved, and a note for you." />
                </td>
              </tr>
              <tr>
                <td><span className={css.stepNumber}>4</span></td>
                <td>
                  <FormattedMessage id="TrackYourPath.step4.what" defaultMessage="Progress bars fill up" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.step4.desc" defaultMessage="Each of the five skills has its own progress bar showing how far through the current level your child is. You can see it any time." />
                </td>
              </tr>
              <tr>
                <td><span className={css.stepNumber}>5</span></td>
                <td>
                  <FormattedMessage id="TrackYourPath.step5.what" defaultMessage="A teacher confirms the level-up" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.step5.desc" defaultMessage="At around 80% of a level's objectives, the skill becomes eligible. The tutor confirms it. Never the software." />
                </td>
              </tr>
              <tr>
                <td><span className={css.stepNumber}>6</span></td>
                <td>
                  <FormattedMessage id="TrackYourPath.step6.what" defaultMessage="A certificate" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.step6.desc" defaultMessage="When your child completes a level, we issue a certificate naming the level and the skill. Yours to keep, print, and send to the people who will be proudest of it." />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Parent dashboard ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="TrackYourPath.dashboard.title"
            defaultMessage="What you see in your parent dashboard"
          />
        </h2>

        <ul className={css.dashboardList}>
          <li className={css.dashboardItem}>
            <FormattedMessage
              id="TrackYourPath.dashboard.item1"
              defaultMessage="Your child's current level in each of the five skills"
            />
          </li>
          <li className={css.dashboardItem}>
            <FormattedMessage
              id="TrackYourPath.dashboard.item2"
              defaultMessage="A progress bar per skill, showing how far through the current level they are"
            />
          </li>
          <li className={css.dashboardItem}>
            <FormattedMessage
              id="TrackYourPath.dashboard.item3"
              defaultMessage="Every lesson report, in order, with the tutor's notes"
            />
          </li>
          <li className={css.dashboardItem}>
            <FormattedMessage
              id="TrackYourPath.dashboard.item4"
              defaultMessage="Which objectives have been achieved and which are still being worked on"
            />
          </li>
          <li className={css.dashboardItem}>
            <FormattedMessage
              id="TrackYourPath.dashboard.item5"
              defaultMessage="Upcoming lessons, past lessons, and your wallet balance"
            />
          </li>
          <li className={css.dashboardItem}>
            <FormattedMessage
              id="TrackYourPath.dashboard.item6"
              defaultMessage="More than one child? Each has their own profile and their own progress, under one parent account."
            />
          </li>
        </ul>
      </section>

      {/* ── Inbox / emails ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="TrackYourPath.inbox.title"
            defaultMessage="What lands in your inbox"
          />
        </h2>

        <div className={css.tableWrapper}>
          <table className={`${css.table} ${css.inboxTable}`}>
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="TrackYourPath.inbox.when" defaultMessage="When" />
                </th>
                <th>
                  <FormattedMessage id="TrackYourPath.inbox.receive" defaultMessage="What you receive" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.trial.when" defaultMessage="After the free trial" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.trial.what" defaultMessage="Your child's starting level across the five skills, and the tutor's recommended focus" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.lesson.when" defaultMessage="After every lesson" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.lesson.what" defaultMessage="A summary of what was taught and the tutor's note to you" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.levelup.when" defaultMessage="On a level-up" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.levelup.what" defaultMessage="Confirmation that a skill has advanced, and the certificate" />
                </td>
              </tr>
              <tr>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.term.when" defaultMessage="Each term" />
                </td>
                <td>
                  <FormattedMessage id="TrackYourPath.inbox.term.what" defaultMessage="A progress summary: what has changed, what comes next" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Why a teacher confirms ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="TrackYourPath.teacher.title"
            defaultMessage="Why a teacher confirms every step"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="TrackYourPath.teacher.p1"
            defaultMessage="Software can count. It cannot listen to a seven-year-old read a sentence and know whether she understood it or guessed."
          />
        </p>
        <p className={css.sectionText}>
          <FormattedMessage
            id="TrackYourPath.teacher.p2"
            defaultMessage="At Arabibi a level-up is always confirmed by the qualified teacher who has taught your child. It is slower than an automatic system and it is deliberately so. When you are told your daughter has reached A2 in speaking, a person is standing behind that statement."
          />
        </p>
      </section>

      {/* ── For your child ── */}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage
            id="TrackYourPath.child.title"
            defaultMessage="For your child"
          />
        </h2>
        <p className={css.sectionText}>
          <FormattedMessage
            id="TrackYourPath.child.p1"
            defaultMessage="Children see a simpler version: their own progress, the objectives they have achieved, and their weekly streak. Attending at least one lesson a week builds a streak, and streaks earn badges:"
          />
        </p>

        <div className={css.badgeRow}>
          <span className={css.badge}>
            Learner's Spark <span className={css.badgeWeeks}>2 weeks</span>
          </span>
          <span className={css.badge}>
            Weekly Warrior <span className={css.badgeWeeks}>4 weeks</span>
          </span>
          <span className={css.badge}>
            Steady Speaker <span className={css.badgeWeeks}>8 weeks</span>
          </span>
          <span className={css.badge}>
            Fluency Champion <span className={css.badgeWeeks}>16 weeks</span>
          </span>
        </div>

        <p className={css.sectionText}>
          <FormattedMessage
            id="TrackYourPath.child.stars"
            defaultMessage="Stars are earned for completing lessons and leaving reviews. They are for encouragement only — they have no monetary value and never expire."
          />
        </p>
      </section>

      {/* ── Plateau / stalls closing ── */}
      <div className={css.closingSection}>
        <h2 className={css.closingTitle}>
          <FormattedMessage
            id="TrackYourPath.stalls.title"
            defaultMessage="And if progress stalls"
          />
        </h2>
        <p className={css.closingText}>
          <FormattedMessage
            id="TrackYourPath.stalls.p1"
            defaultMessage="Sometimes a child plateaus. It happens in every language, and it is usually a sign that something needs to change — a different focus, a different pace, sometimes a different tutor."
          />
        </p>
        <p className={css.closingText}>
          <FormattedMessage
            id="TrackYourPath.stalls.p2"
            defaultMessage="Because everything is tracked per skill, you will see it early rather than after six months of paying. Tell us, and we will help you change it."
          />
        </p>
      </div>
    </article>
  );
};

export default TrackYourPath;
